import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RowSelectionState } from '@tanstack/react-table';
import { Expand, Plus, Shrink, Trash2 } from 'lucide-react';

import BerthPlan, {
  BerthPlanEditing,
} from '@/components/blocks/berth-plan/berth-plan';
import { isLockedVisit } from '@/components/blocks/berth-plan/berth-plan.utils';
import BerthPlanViewToggle from '@/components/blocks/berth-plan/berth-plan-view-toggle';
import { VisitEditorOptions } from '@/components/blocks/berth-plan/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { useOpen } from '@/hooks/use-open';
import { cn } from '@/lib/utils';
import CreateShipVisitDialog from '@/routes/(app)/(scenarios)/(create)/components/create-ship-visit-dialog';
import DeleteShipVisitsDialog from '@/routes/(app)/(scenarios)/(create)/components/delete-ship-visits-dialog';
import ShipVisitEditor from '@/routes/(app)/(scenarios)/(create)/components/ship-visit-editor';
import { getSelectedVisitIds } from '@/routes/(app)/(scenarios)/(create)/components/shipment-planning.utils';
import ShipmentPlanningTable from '@/routes/(app)/(scenarios)/(create)/components/shipment-planning-table';
import { usePlanningVisits } from '@/routes/(app)/(scenarios)/(create)/hooks/use-planning-visits';
import {
  CreateShipVisitSchema,
  ShipVisit,
} from '@/routes/(app)/(scenarios)/(create)/types';

interface ShipmentPlanningEditorProps {
  planningId: string;
  cutoffMs?: number;
}

const ShipmentPlanningEditor: FC<ShipmentPlanningEditorProps> = ({
  planningId,
  cutoffMs,
}) => {
  const { t } = useTranslation();
  const {
    planning,
    isLoading,
    isSaving,
    commitVisits,
    updateVisit,
    moveVisit,
  } = usePlanningVisits(planningId);
  // Shows every visit; Locked Visits are greyed out and read-only.
  const visits = useMemo(
    () => planning?.ship_visits ?? [],
    [planning?.ship_visits]
  );
  const [expanded, setExpanded] = useState(false);
  // The editable table needs the full width, so it is a view of its own.
  const [isTableView, setIsTableView] = useState(false);
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);
  const createShipVisitDialog = useOpen({ defaultOpen: false });
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  // Visits awaiting delete confirmation, from the table selection or a popover.
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Array<string>>([]);

  const selectedVisitIds = useMemo(
    () => getSelectedVisitIds(rowSelection),
    [rowSelection]
  );

  const handleCreateShipVisit = useCallback(
    async (data: CreateShipVisitSchema) => {
      const newVisit: ShipVisit = {
        ...data,
        // Server-computed berth meters fill in when the planning refetches.
        berth_position_start_meter: null,
        berth_position_end_meter: null,
        berth_length_meter: null,
      };
      await commitVisits((current) => [...current, newVisit]);
      createShipVisitDialog.onOpenChange(false);
    },
    [commitVisits, createShipVisitDialog]
  );

  const handleDeleteShipVisits = useCallback(async () => {
    if (pendingDeleteIds.length === 0) return;

    const deleteIds = new Set(pendingDeleteIds);
    setPendingDeleteIds([]);
    setRowSelection({});
    await commitVisits((current) =>
      current.filter(
        (visit) => !deleteIds.has(visit.id) || isLockedVisit(visit, cutoffMs)
      )
    );
  }, [pendingDeleteIds, commitVisits, cutoffMs]);

  const deselectVisit = useCallback(() => {
    setSelectedVisitId(null);
  }, []);

  const editing = useMemo<BerthPlanEditing>(
    () => ({
      onMoveVisit: moveVisit,
      renderEditor: (visitId: string, options: VisitEditorOptions) => {
        const visit = visits.find((candidate) => candidate.id === visitId);
        if (!visit) return null;
        return (
          <ShipVisitEditor
            // Remounts when the visit moves under an open editor, so the form
            // and its save diff never work from stale values.
            key={[
              visit.arrival_time,
              visit.departure_time,
              visit.bollard_fore,
              visit.bollard_aft,
            ].join('|')}
            visit={visit}
            cutoffMs={cutoffMs}
            hasConflict={options.hasConflict}
            canEditBollards={options.canEditBollards}
            onSave={async (patch) => updateVisit(visit.id, patch)}
            onDelete={() => {
              setSelectedVisitId(null);
              setPendingDeleteIds([visit.id]);
            }}
            onClose={deselectVisit}
          />
        );
      },
    }),
    [visits, cutoffMs, moveVisit, updateVisit, deselectVisit]
  );

  return (
    <>
      <Card
        className={cn(
          'col-span-full flex min-h-0 flex-1',
          expanded &&
            'fixed top-0 left-0 w-full h-full z-100 rounded-none overflow-scroll'
        )}>
        <CardHeader className='flex flex-row flex-wrap items-center justify-between gap-2'>
          <BerthPlanViewToggle
            isTableView={isTableView}
            onTableViewChange={setIsTableView}
          />
          <div className='flex flex-wrap items-center gap-2'>
            {isTableView && selectedVisitIds.length > 0 && (
              <Button
                variant='outline'
                className='border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive'
                disabled={isSaving}
                onClick={() => {
                  setPendingDeleteIds(selectedVisitIds);
                }}>
                <Trash2 />
                {t('scenarios.create.customizePlanning.delete', {
                  count: selectedVisitIds.length,
                })}
              </Button>
            )}
            <Button
              variant='outline'
              onClick={() => {
                createShipVisitDialog.onOpenChange(true);
              }}>
              <Plus />
              {t('scenarios.create.customizePlanning.add')}
            </Button>
            <Button
              aria-label={t('scenarios.create.customizePlanning.expand')}
              onClick={() => {
                setExpanded((prev) => !prev);
              }}>
              <Button.Icon icon>
                {expanded ? <Shrink /> : <Expand />}
              </Button.Icon>
            </Button>
          </div>
        </CardHeader>
        <CardContent className='flex min-h-0 flex-1 flex-col'>
          {isLoading && <Spinner />}
          {!isLoading && (
            <div className='flex min-h-0 flex-1 flex-col gap-4'>
              {isTableView ? (
                <div className='min-h-0 w-full flex-1 overflow-auto'>
                  <ShipmentPlanningTable
                    shipVisits={visits}
                    cutoffMs={cutoffMs}
                    onUpdateVisit={updateVisit}
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection}
                  />
                </div>
              ) : (
                <BerthPlan
                  visits={visits}
                  cutoffMs={cutoffMs}
                  selectedVisitId={selectedVisitId}
                  onSelectVisit={setSelectedVisitId}
                  onDeselect={deselectVisit}
                  editing={editing}
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>
      <CreateShipVisitDialog
        onSubmit={handleCreateShipVisit}
        open={createShipVisitDialog.open}
        onOpenChange={createShipVisitDialog.onOpenChange}
        cutoffMs={cutoffMs}
      />
      <DeleteShipVisitsDialog
        open={pendingDeleteIds.length > 0}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteIds([]);
        }}
        onConfirm={handleDeleteShipVisits}
        count={pendingDeleteIds.length}
      />
    </>
  );
};

export default ShipmentPlanningEditor;
